'use client';

import clsx from 'clsx';
import Image from 'next/image';
import React, { useRef } from 'react';

import { Button } from '@/shared/ui';
import ChevronLeftIcon from './assets/chevron_left.svg';
import ChevronRightIcon from './assets/chevron_right.svg';
import styles from './Slider.module.css';

type SliderProps = {
  children: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
};

export const Slider = ({ children, className, header }: SliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  if (!children) return null;

  const childrenArray = React.Children.toArray(children);

  const scrollToNext = () => {
    if (!sliderRef.current) return;

    const container = sliderRef.current;
    const itemWidth = container.firstElementChild?.clientWidth || 0;
    const scrollAmount = container.scrollLeft + itemWidth;

    container.scrollTo({
      left: scrollAmount,
      behavior: 'smooth',
    });
  };

  const scrollToPrevious = () => {
    if (!sliderRef.current) return;

    const container = sliderRef.current;
    const itemWidth = container.firstElementChild?.clientWidth || 0;
    const scrollAmount = container.scrollLeft - itemWidth;

    container.scrollTo({
      left: scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.header}>
        {header}
        <div className={styles.controls}>
          <Button variant="icon" className={styles.button} onClick={scrollToPrevious}>
            <Image width={36} height={36} src={ChevronLeftIcon} alt="Previous slide" />
          </Button>
          <Button variant="icon" className={styles.button} onClick={scrollToNext}>
            <Image width={36} height={36} src={ChevronRightIcon} alt="Next slide" />
          </Button>
        </div>
      </div>
      <div ref={sliderRef} className={styles.wrapper}>
        {childrenArray.map((child, index) => (
          <div className={styles.item} key={index}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};
